/**
 * Context to share viewsID, filterData across the component
 *
 * activeView and setActiveView is removed and activeView is now set in appliedFiltersCtxt
 * This context is handling the activeView from the URL query params
 * and setting it in the appliedFiltersCtxt state.
 *
 * Usages-
 *
 * appliedFiltersCtxt - used by jobs, filters, and views as well
 * filteredJobCount - Ref to store Displayed/filtered job count in the filter context
 *
 *
 * Usage of Ref and States
 *
 * use states when -
 * - change in variable needs a re-render in every component this context is used
 * - change in variable needs to run a useEffect
 *
 * use ref when -
 * - changes in the variable is only used on a function call, or a action
 * - to skip a re-render, and is not used for UI purposes.
 * - jobSelectedKeywordsRef is a ref to store selected keywords and jobs, as this is not used for UI purposes and only used when user does an action
 *
 * from date and to date are set to current campaign start and end date, as we know these would be default if not changed
 * if changed, they will be given priority over the campaign dates from the session
 */
import React, { useState, useContext, createContext, useEffect, useRef, memo } from "react";
import { useSessionContext } from "@/contexts/sessionContext";
import { SortGroups } from "@/config/Columns";
import { useSearchParams,useLocation } from "react-router";
import { RouteMap } from "@/config/RouteMap";

export const ViewsFilterContext = createContext({
  appliedFiltersCtxt: {},
  setAppliedFiltersCtxt: () => {},
  updateViews: () => {},
  filteredJobCount: { current: 0 },
  noFilter: false,
  noView: false,
  noSearch: false,
  filterComponentRef: null,
  arrInnovationLabTeaserData: {},
  selectedDatePickerCampaignRef: null,
  featureNoticeData: {},
  setFeatureNoticeData: () => {},
  warningJobsOnlyRef: {},
  jobSelectedKeywordsRef: {},
});

export const useViewsFilterContext = () => useContext(ViewsFilterContext);

export const ViewsFiltersContextProvider = memo(({ children }) => {
  const { session: UserSession } = useSessionContext();
  /**
   * we want this, so viewsFiltersContextProvider re-renders when search params change
   * but, we dont use the searchParams object
   * as its runs late
   */
  useSearchParams();
  const queryParams = new URLSearchParams(window.location.search);
  const noFilter = !!parseInt(queryParams.get("nofilter"));
  const noView = !!parseInt(queryParams.get("noview"));
  const noSearch = !!parseInt(queryParams.get("nosearch"));
  const location = useLocation();
  const filteredJobCount = useRef(0);
  const filterComponentRef = useRef(null);
  const selectedDatePickerCampaignRef = useRef(null);
  const [arrInnovationLabTeaserData, setarrInnovationLabTeaserData] = useState({});
  const [featureNoticeData, setFeatureNoticeData] = useState({});
  const warningJobsOnlyRef = useRef({
    isViewApplied: false,
    isFilterApplied: false,
    isSearchApplied: false,
    isJobTabsChanged: false,
    isQuickFilterApplied: false,
    isViewChanged: false,
    isMainTabChanged: false,
  });
  const jobSelectedKeywordsRef = useRef({ keywords: [], selectedJobs: [] }); // To store selected keywords for jobs

  useEffect(() => {
    if (
      location &&
      !(
        location?.pathname.includes(RouteMap.myjobs) ||
        location?.pathname.includes(RouteMap.job_portfolio) ||
        location?.pathname.includes(RouteMap.myjobs_config) ||
        location?.pathname.includes(RouteMap.postings_overview) ||
        (location?.pathname.includes(RouteMap.sourcingBuilder) && !!queryParams.get("keyword"))
      )
    ) {
      jobSelectedKeywordsRef.current = { keywords: [], selectedJobs: [] };
    }

    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, queryParams]);
  // Cleared selected keywords if we are not on the conditioned pages

  

  // const defaultViewID =  || null;
  const [appliedFiltersCtxt, setAppliedFiltersCtxt_org] = useState({
    sortKey:
      UserSession.UIPreferences?.page_preferences?.v2_ticket_admin?.sort?.sortKey ||
      `basicData-LastUpdate`,
    sortOrder: UserSession.UIPreferences?.page_preferences?.v2_ticket_admin?.sort?.sortOrder || "desc",
    postingsSortKey:
      UserSession.UIPreferences?.page_preferences?.v2_ticket_admin?.postings?.sortKey || `clicks-budget-selectedPeriod`,
    postingsSortOrder: UserSession.UIPreferences?.page_preferences?.v2_ticket_admin?.postings?.sortOrder || "desc",
    postingsShowValue:
      UserSession.UIPreferences?.page_preferences?.v2_ticket_admin?.postings?.postingsShowValue ||
      "clicks-budget-selectedPeriod",
    filterSelections: { filterValues: (() => {
      const sessionFilter = UserSession.UIPreferences?.page_preferences?.v2_ticket_admin?.filter;
      if (sessionFilter && typeof sessionFilter === "object") {
        const { filterString, ...filterValues } = sessionFilter;
        if (Object.keys(filterValues).length > 0) return filterValues;
      }
      return { New: true };
    })() },
    filterString: UserSession.UIPreferences?.page_preferences?.v2_ticket_admin?.filter?.filterString === "" || UserSession.UIPreferences?.page_preferences?.v2_ticket_admin?.filter?.filterString === undefined ? "searchtitle=&filter%5BNew%5D=true" : UserSession.UIPreferences?.page_preferences?.v2_ticket_admin?.filter?.filterString,
    from_date:
      UserSession.UIPreferences?.page_preferences?.v2_global?.dateRange?.from_date ||
      new Date(UserSession.CampaignDetails?.currentCampaignStartDate).getTime(),
    to_date:
      UserSession.UIPreferences?.page_preferences?.v2_global?.dateRange?.to_date ||
      new Date(UserSession.CampaignDetails?.currentCampaignEndDate).getTime(),
    searchObject: UserSession.UIPreferences?.page_preferences?.v2_global?.searchObject ?? { label: "", value: "" },
  });
  const setAppliedFiltersCtxt = (data = {}) => {
    setAppliedFiltersCtxt_org((prev) => ({ ...prev, ...data }));
  };

 



  return (
    <ViewsFilterContext
      value={{
        appliedFiltersCtxt,
        setAppliedFiltersCtxt,
        filteredJobCount: filteredJobCount,
        noFilter: noFilter,
        noView: noView,
        noSearch: noSearch,
        filterComponentRef: filterComponentRef,
        arrInnovationLabTeaserData: arrInnovationLabTeaserData,
        selectedDatePickerCampaignRef: selectedDatePickerCampaignRef,
        featureNoticeData: featureNoticeData,
        setFeatureNoticeData: setFeatureNoticeData,
        warningJobsOnlyRef: warningJobsOnlyRef,
        jobSelectedKeywordsRef: jobSelectedKeywordsRef,
      }}
    >
      {children}
    </ViewsFilterContext>
  );
});
